import React, { FunctionComponent, useState, useEffect } from 'react';
import UploadBox from '../components/UploadBox';
import ModalBase from '../components/ModalBase';
import FormField, { FieldOption } from '../components/FormField';
import ExplanationField from '../components/ExplanationField';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { FileFormat } from '../types';
import { useUploadContext } from '../contexts/UploadContext';
import useURLParams from '../hooks/useURLParams';
import { fetchUploadToken, reviewLabel } from '../utils/backendServices';
import axios from 'axios';
import { useProjectDetailsContext } from '../contexts/ProjectDetailsContext';

interface UploadLabelProps {
    projectId: string;
}

const UploadLabel: FunctionComponent<UploadLabelProps> = ({ projectId }) => {
    const [versionName, setVersionName] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedPdfPage, setSelectedPdfPage] = useState<number>();
    const [selectedPackCopyId, setSelectedPackCopyId] = useState<string>('');
    const [canSubmit, setCanSubmit] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const { setParam } = useURLParams();
    const { triggerFetch } = useUploadContext();
    const {
        state: { packCopies },
    } = useProjectDetailsContext();

    const packCopyOptions: FieldOption[] = packCopies
        ? packCopies.map((pc) => ({
              value: pc.id,
              label: `${pc.name} (${pc.id})`,
          }))
        : [];

    useEffect(() => {
        setCanSubmit(
            !!versionName &&
                !!selectedFile &&
                !!selectedPackCopyId &&
                !isUploading,
        );
    }, [versionName, selectedFile, selectedPackCopyId, isUploading]);

    const handleUpload = async () => {
        if (!selectedFile || !selectedPackCopyId) return;

        setIsUploading(true);

        try {
            const fileFormat = selectedFile.name
                .split('.')
                .pop()
                ?.toLowerCase() as FileFormat;

            if (fileFormat && Object.values(FileFormat).includes(fileFormat)) {
                const { url, unique_id } = await fetchUploadToken(
                    fileFormat,
                    versionName,
                    'label',
                    projectId,
                );
                triggerFetch();
                await axios.put(url, selectedFile, {
                    headers: { 'x-ms-blob-type': 'BlockBlob' },
                });

                let fileConfig;

                if (
                    fileFormat === FileFormat.JPG ||
                    fileFormat === FileFormat.JPEG ||
                    fileFormat === FileFormat.PNG
                ) {
                    fileConfig = {
                        file_format: fileFormat,
                    };
                } else if (selectedPdfPage && fileFormat === FileFormat.PDF) {
                    fileConfig = {
                        file_format: fileFormat,
                        page: selectedPdfPage,
                    };
                }

                if (fileConfig) {
                    await reviewLabel(unique_id, {
                        file_config: fileConfig,
                        properties: {
                            version_name: versionName,
                            notes: notes || undefined,
                            project_id: projectId,
                            pack_copy_id: selectedPackCopyId,
                        },
                    });
                }

                setSelectedFile(null);
                setVersionName('');
                setNotes('');
                setSelectedPackCopyId('');
                setParam('modal', null);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            // TODO: Show error message to user
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <ModalBase onClose={() => setParam('modal', null)} width="500px">
            <div className="upload-components">
                <h2 className="modal-title">Upload New Label</h2>
                <p className="modal-description">
                    Upload your label as a PDF or image and fill out the
                    required fields.
                </p>

                <UploadBox
                    setSelectedFile={setSelectedFile}
                    selectedFile={selectedFile}
                    setSelectedPdfPage={setSelectedPdfPage}
                    allowImageUpload={true}
                    allowPdfUpload={true}
                    requireSinglePdfPage={true}
                    allowWordUpload={false}
                    allowExcelUpload={false}
                    allowCsvUpload={false}
                    maxFileSizeMB={10}
                    minImageDimension={2000}
                />

                <div className="form-field-container">
                    <FormField
                        id="versionName"
                        label="Version Name"
                        value={versionName}
                        onChange={(e) => setVersionName(e.target.value)}
                        placeholder="Enter a name for this label version"
                        required={true}
                        type="text"
                    />

                    <FormField
                        id="packCopy"
                        label="Pack Copy"
                        value={selectedPackCopyId}
                        onChange={(e) => setSelectedPackCopyId(e.target.value)}
                        required={true}
                        type="dropdown"
                        options={packCopyOptions}
                        emptyOptionLabel="Select a Pack Copy"
                    />
                    <ExplanationField>
                        <p>
                            Select the corresponding pack copy to let the AI
                            check if the label text matches the specifications.
                        </p>
                    </ExplanationField>

                    {(!packCopies || packCopies.length === 0) && (
                        <div
                            className="warning-message"
                            style={{ marginTop: '-10px', marginBottom: '10px' }}
                        >
                            You need to upload a Pack Copy first to review the
                            label text.
                        </div>
                    )}

                    <FormField
                        id="notes"
                        label="Notes"
                        type="textarea"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any notes or comments (optional)"
                        rows={3}
                        required={false}
                    />
                </div>

                <div className="button-row">
                    <button
                        className="btn-secondary"
                        onClick={() => setParam('modal', null)}
                        disabled={isUploading}
                    >
                        Cancel
                    </button>
                    <SignedIn>
                        <button
                            className="btn-primary"
                            onClick={handleUpload}
                            disabled={!canSubmit}
                        >
                            {isUploading ? 'Checking...' : 'Check Label'}
                        </button>
                    </SignedIn>
                    <SignedOut>
                        <SignInButton>
                            <button
                                className="btn-primary"
                                disabled={!canSubmit}
                            >
                                {isUploading ? 'Checking...' : 'Check Label'}
                            </button>
                        </SignInButton>
                    </SignedOut>
                </div>
            </div>
        </ModalBase>
    );
};

export default UploadLabel;
